import { useState, useEffect } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { Plus, LayoutGrid, List } from "lucide-react";
import { arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { restrictToParentElement } from "@dnd-kit/modifiers";
import { CategoryList } from "../Component/CategoryList";
import ProductList from "../Component/ProductList";
import { AddMenuItemForm } from "../Component/AddMenuItemForm";
import { AddCategoryForm } from "../Component/AddCategoryForm";
import store from "../store";
import { useCloud } from "../hooks/useCloud";
import toast from "react-hot-toast";
import { SUCCESS } from "../assets/constants";

export default function MenuManagement() {
  const {
    addItemAPI,
    updateItem,
    deleteItem,
    categoryDelete,
    categoryUpdate,
    addCategoryAPI,
    outOfStock,
    getMenu,
    createOrUpdateCategory,
    createOrUpdateMenuItem,
    deleteMenuItem,
    deleteCategory,
  } = useCloud();
  // Sample data
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [productMap, setProductMap] = useState({});
  const [selectedCategory, setSelectedCategory] = useState(categories[0]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [isProductFormOpen, setIsProductFormOpen] = useState(false);
  const [isCategoryFormOpen, setIsCategoryFormOpen] = useState(false);
  const [forceUpdateCount, setForeceUpdateCount] = useState(0);
  const [viewMode, setViewMode] = useState("card");
  const [isCategoryShufelled, setIsCategoryShufelled] = useState(false);
  const [loading, setLoading] = useState(false);
  const processMenu = (menu) => {
    let { products, categories } = menu.reduce(
      (acc, category) => {
        acc.products.push(
          ...category.menuItems.map((item) => {
            item.category = category.categoryName;
            item.categoryDetails = category;
            return item;
          })
        );
        if (
          !acc.categories.some(
            (_category) => _category.categoryId == category.categoryId
          )
        )
          acc.categories.push(category);
        return acc;
      },
      {
        products: [],
        categories: [],
      }
    );

    categories = categories.sort((a, b) => a.categoryRank - b.categoryRank);
    setLoading(false);
    return { products, categories };
  };

  const fetchMenu = async () => {
    setLoading(true);
    const menu = await getMenu();
    if (menu && menu.status === SUCCESS) {
      const { data } = menu;

      if (data.length <= 0) {
        return;
      }

      const { products, categories } = processMenu(data);
      const productDeatilsMap = products.reduce((acc, product) => {
        acc[product.id] = product;
        return acc;
      }, {});

      setCategories(categories);
      setSelectedCategory(categories[0]);
      setProducts(products);
      setProductMap(
        products.reduce((acc, item) => {
          acc[item.id] = item;
          return acc;
        }, {})
      );
      setFilteredProducts(
        products.filter(
          (product) => product.categoryId == selectedCategory?.categoryId
        )
      );
      // setProductDetailsMap(productDeatilsMap);
    }
  };

  useEffect(() => {
    const mode = localStorage.getItem("menu-view");
    if (mode) setViewMode(mode);
  }, []);

  useEffect(() => {
    fetchMenu();
  }, []);

  useEffect(() => {
    setFilteredProducts(
      products.filter(
        (product) => product.categoryId == selectedCategory?.categoryId
      )
    );
  }, [selectedCategory, products]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleChangeViewMode = (mode) => {
    localStorage.setItem("menu-view", mode);
    setViewMode(mode);
  };

  const handleCategoryDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setIsCategoryShufelled(true);
      setCategories((items) => {
        const oldIndex = items.findIndex(
          (item) => item.categoryId === active.id
        );
        const newIndex = items.findIndex((item) => item.categoryId === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleProductDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setProducts((items) => {
        const oldIndex = items.findIndex(
          (item) => item.id === Number(active.id)
        );
        const newIndex = items.findIndex((item) => item.id === Number(over.id));

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleEditProduct = (id) => {
    // Implement edit functionality
    console.log(`Edit product with id: ${id}`);
    console.log(productMap[id]);
    setSelectedProduct(productMap[id]);
    setIsProductFormOpen(true);
  };

  const handleDeleteProduct = async (id) => {
    const { status, data } = await deleteMenuItem(id);
    if (status === SUCCESS) {
      setProducts(products.filter((product) => product.id !== id));
      toast.success("Menu Item deleted successfully");
    } else {
      toast.error("Failed! Please try again");
    }
  };

  const updateProductList = (products, updatedItem) => {
    return products.map((item) =>
      item.id === updatedItem.id ? updatedItem : item
    );
  };

  const addItemToProductMap = (item) => {
    setProductMap((prev) => ({
      ...prev,
      [item.id]: item,
    }));
  };

  const handleAPIResponse = (newProduct, response, isUpdate = false) => {
    const { menuItem, event, msg = "", error = "" } = response;

    if (event) {
      const updatedItem = {
        ...newProduct,
        ...menuItem,
        amount: menuItem.price,
      };

      if (isUpdate) {
        setProducts((prev) => updateProductList(prev, updatedItem));
      } else {
        setProducts((prev) => [...prev, updatedItem]);
      }

      addItemToProductMap(updatedItem);
      store.addItems(
        isUpdate
          ? updateProductList(products, updatedItem)
          : [...products, updatedItem]
      );

      toast.success(msg);
    } else {
      toast.error(error);
    }
  };

  const handleAddProduct = async (newProduct) => {
    if (selectedProduct) newProduct["id"] = selectedProduct.id;

    const { status, data } = await createOrUpdateMenuItem(newProduct);

    console.log(status, data);

    if (status === SUCCESS) {
      setProducts((prev) => {
        if (selectedProduct) {
          toast.success("Menu updated successfully");
          return prev.map((menuItem) => {
            if (menuItem.id === newProduct.id) {
              return newProduct;
            }
            return menuItem;
          });
        } else {
          toast.success("Menu added successfully");
          return [...prev, { ...newProduct, id: data.menuId }];
        }
      });
    }
    setIsProductFormOpen(false);
  };

  const handleAddCategory = async (newCategory) => {
    console.log(newCategory);
    if (selectedCategory)
      newCategory["categoryId"] = selectedCategory.categoryId;

    const { status, data } = await createOrUpdateCategory(newCategory);

    if (status === SUCCESS) {
      setCategories((prev) => {
        if (selectedCategory) {
          toast.success("Category updated successfully");
          return prev.map((category) => {
            if (category.categoryId === newCategory.categoryId) {
              return newCategory;
            }
            return category;
          });
        } else {
          const category = { ...newCategory, categoryId: data.categoryId };
          toast.success("Category added successfully");
          setSelectedCategory(category);
          return [...prev, { ...newCategory, ...category }];
        }
      });
      setIsCategoryFormOpen(false);
    }
  };

  const handleCategoryEdit = (category) => {
    setIsCategoryFormOpen(true);
  };
  const handleCategoryDelete = async (categoryId) => {
    console.log(categoryId);

    const { status, data } = await deleteCategory(categoryId);
    if (status === SUCCESS) {
      const filterdCategories = categories.filter(
        (category) => category.categoryId !== categoryId
      );
      setCategories(filterdCategories);
      setSelectedCategory(filterdCategories[0]);
      toast.success("Menu Item deleted successfully");
    } else {
      toast.error("Failed! Please try again");
    }
  };

  const handelCategoryStatusUpdate = async (category, updateStatus) => {
    const newCategory = {
      categoryId: category.categoryId,
      categoryName: category.categoryName,
      image: category.categoryImage,
      status: updateStatus,
    };

    const { status, data } = await createOrUpdateCategory(newCategory);

    if (status === SUCCESS) {
      setCategories((prev) => {
        if (selectedCategory) {
          toast.success("Category updated successfully");
          return prev.map((category) => {
            if (category.categoryId === newCategory.categoryId) {
              return newCategory;
            }
            return category;
          });
        } else {
          const category = { ...newCategory, categoryId: data.categoryId };
          toast.success("Category added successfully");
          setSelectedCategory(category);
          return [...prev, { ...newCategory, category }];
        }
      });
      setIsCategoryFormOpen(false);
    }
  };

  const handleUpdateCategory = (category) => {
    console.log(category);
    // const res = await categoryUpdate({
    //   name: form.name,
    //   categoryId: form.id,
    //   url: form.url,
    // });
  };

  const handleCloseAddProductForm = () => {
    setIsProductFormOpen(false);
    setSelectedProduct(null);
  };

  const toggleStock = async (product) => {
    const { id, categoryId } = product;
    const status = product.status === 'active' ? 'inactive' : 'active'
    const { status: responseStatus } = await outOfStock({ itemId: id, categoryId: categoryId, status });
    if (responseStatus === SUCCESS) {
      setProducts((prev) => {
        prev.forEach((item) => {
          if (item.id === id) {
            item.status = status;
          }
        });
        return prev;
      });

      setForeceUpdateCount((prev) => prev + 1);
      toast.success("Stock updated");
    } else {
      toast.error('Please try again');
    }
  };

  const handleRankSave = async () => {
    const responses = await Promise.all(
      categories.map((category, index) => {
        const newCategory = {
          categoryId: category.categoryId,
          categoryName: category.categoryName,
          image: category.categoryImage,
          status: category.status,
          rank: index,
        };

        return createOrUpdateCategory(newCategory);
      })
    );

    if (responses.length > 0) {
      await fetchMenu();
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2  border-green-900"></div>
        <p className="text-2xl font-bold">Loading...</p>
        <p className="text-sm text-gray-500">Please wait while we load the menu</p>
      </div>
    );
  }

  return (

    <div className="flex">
      <div className="flex flex-1 flex-col">
        <header className="sticky top-0 z-10 flex h-16 items-center gap-4 border-b bg-white px-6">
          <h1 className="text-xl font-semibold">Menu Management</h1>
        </header>

        <div className="bg-gray-50 ">
          <div className="max-w-[1400px] h-[calc(100vh-64px)] w-[100vw] md:w-[calc(100vw-120px)] mx-auto p-4 box-border bg-gray-50 overflow-x-hidden relative hide-scrollbar">
            <div className="flex flex-1 flex-col gap-4 p-4 md:gap-8 md:p-8">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-[220px_1fr]">
                <div className="flex flex-col space-y-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-medium">Category</h2>
                    {isCategoryShufelled ? (
                      <button
                        onClick={() => {
                          handleRankSave();
                          setIsCategoryShufelled(false);
                        }}
                        className="flex items-center justify-center h-8 px-1 text-sm border bg-black text-white border-gray-300 rounded-md hover:border-blue-400"
                      >
                        Save order
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          setSelectedCategory(null);
                          setIsCategoryFormOpen(true);
                        }}
                        className="flex bg-white items-center justify-center h-8 px-1 text-sm border  border-gray-300 rounded-md hover:border-gray-400 hover:bg-gray-50"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Category
                      </button>
                    )}
                  </div>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleCategoryDragEnd}
                    modifiers={[restrictToParentElement]}
                  >
                    <CategoryList
                      categories={categories}
                      selectedCategory={selectedCategory}
                      onSelectCategory={setSelectedCategory}
                      onEdit={handleCategoryEdit}
                      onDelete={handleCategoryDelete}
                      onStatusChange={handelCategoryStatusUpdate}
                    />
                  </DndContext>
                </div>
                <div className="flex flex-col space-y-4">
                  <div className="md:flex items-center justify-between">
                    <h2 className="text-lg font-medium">Products</h2>
                    <div className="flex  gap-2">
                      <button
                        onClick={() => setIsProductFormOpen(true)}
                        className="flex bg-white items-center justify-center h-8 px-3 text-sm border  border-gray-300 rounded-md hover:border-gray-400 hover:bg-gray-50"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Food Item
                      </button>

                      <div className="inline-flex rounded-md shadow-sm">
                        <button
                          type="button"
                          onClick={() => handleChangeViewMode("card")}
                          className={`relative inline-flex items-center px-3 py-2 text-sm font-medium rounded-l-md ${
                            viewMode === "card"
                              ? "bg-gray-500 text-white"
                              : "bg-white text-gray-700 hover:bg-gray-50"
                          } border border-gray-300`}
                        >
                          <LayoutGrid className="h-4 w-4 mr-2" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleChangeViewMode("table")}
                          className={`relative inline-flex items-center px-3 py-2 text-sm font-medium rounded-r-md ${
                            viewMode === "table"
                              ? "bg-gray-500 text-white"
                              : "bg-white text-gray-700 hover:bg-gray-50"
                          } border border-gray-300 border-l-0`}
                        >
                          <List className="h-4 w-4 mr-2" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleProductDragEnd}
                    modifiers={[restrictToParentElement]}
                  >
                    <ProductList
                      products={filteredProducts}
                      onEdit={handleEditProduct}
                      onDelete={handleDeleteProduct}
                      viewMode={viewMode}
                      toggleStock={toggleStock}
                    />
                  </DndContext>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Add Product Form Modal */}
      {isProductFormOpen && (
        <AddMenuItemForm
          menuItem={selectedProduct}
          categories={categories}
          onClose={handleCloseAddProductForm}
          onSubmit={handleAddProduct}
        />
      )}

      {/* Add Category Form Modal */}
      {isCategoryFormOpen && (
        <AddCategoryForm
          categoryItem={selectedCategory}
          onClose={() => {
            setSelectedCategory(categories[0]);
            setIsCategoryFormOpen(false);
          }}
          onSubmit={handleAddCategory}
        />
      )}
    </div>
  );
}
